import React, { useState, useEffect } from 'react';
import { Card } from './layout';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useUpdateOrderAttributeValue, useOrderFieldModifications } from '../hooks/use-order-attributes';
import { OrderAttributeFieldDto, UpdateOrderAttributeValueRequest } from '../types';
import { formatDate } from '../utils';

interface FilterableSelectOption {
  name: string;
  value: string;
}

interface FilterableSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: FilterableSelectOption[];
  showValueInList: boolean;
  required?: boolean;
}

function FilterableSelect({ value, onChange, options, showValueInList, required }: FilterableSelectProps) {
  const [filter, setFilter] = useState('');

  const filtered = filter
    ? options.filter(
        (opt) =>
          opt.name.toLowerCase().includes(filter.toLowerCase()) ||
          opt.value.toLowerCase().includes(filter.toLowerCase())
      )
    : options;

  return (
    <div className="space-y-1">
      <div className="relative">
        <input
          type="text"
          placeholder="Filter choices..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="w-full pl-7 pr-2 py-1.5 text-sm border border-gray-300 dark:border-secondary-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
        />
        <i className="fa-solid fa-magnifying-glass absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 dark:text-secondary-500 text-xs pointer-events-none" />
        {filter && (
          <button
            type="button"
            onClick={() => setFilter('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 dark:text-secondary-400 hover:text-gray-700 dark:hover:text-secondary-200 text-xs"
            aria-label="Clear filter"
          >
            <i className="fa-solid fa-xmark" />
          </button>
        )}
      </div>
      <select
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-secondary-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
        required={required}
        size={Math.min(Math.max(filtered.length + 1, 3), 8)}
      >
        <option value="">-- Select --</option>
        {filtered.length === 0 ? (
          <option disabled value="__no_match__">No matches</option>
        ) : (
          filtered.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {showValueInList ? `${opt.name} (${opt.value})` : opt.name}
            </option>
          ))
        )}
      </select>
      {filter && (
        <p className="text-xs text-gray-500 dark:text-secondary-400">
          {filtered.length} of {options.length} shown
        </p>
      )}
    </div>
  );
}

interface OrderAttributesSectionProps {
  workflowId: string;
  orderId: string;
  step: number;
  orderAttributes: OrderAttributeFieldDto[]; // Renamed from editableFields
  onFieldUpdated?: () => void;
  /** Whether the current user is an active pending approver at the current step.
   *  When false, all edit buttons are hidden even if the attribute's step range allows editing. */
  isActiveApprover?: boolean;
}

export function OrderAttributesSection({
  workflowId,
  orderId,
  step,
  orderAttributes,
  onFieldUpdated,
  isActiveApprover = false,
}: OrderAttributesSectionProps) {
  const [editingFieldId, setEditingFieldId] = useState<string | null>(null);
  const [fieldValues, setFieldValues] = useState<Record<string, any>>({});
  const [showHistory, setShowHistory] = useState(false);

  const { data: modifications } = useOrderFieldModifications(
    workflowId,
    orderId,
    showHistory
  );

  const updateFieldMutation = useUpdateOrderAttributeValue(workflowId, orderId);

  // Clear saved field values when orderAttributes updates (after refetch)
  // This ensures we don't keep stale values in state
  useEffect(() => {
    // When orderAttributes updates (refetch complete), clear saved values
    // to use the fresh data from the server
    if (!editingFieldId) {
      setFieldValues({});
    }
  }, [orderAttributes, editingFieldId]);

  const handleStartEdit = (field: OrderAttributeFieldDto) => {
    setEditingFieldId(field.editableFieldId);
    setFieldValues({
      ...fieldValues,
      [field.editableFieldId]: field.currentValue ?? '',
    });
  };

  const handleCancelEdit = (fieldId: string) => {
    setEditingFieldId(null);
    const newValues = { ...fieldValues };
    delete newValues[fieldId];
    setFieldValues(newValues);
  };

  const handleSaveField = async (field: OrderAttributeFieldDto) => {
    const request: UpdateOrderAttributeValueRequest = {
      editableFieldId: field.editableFieldId,
      newValue: fieldValues[field.editableFieldId],
    };

    try {
      const result = await updateFieldMutation.mutateAsync(request);
      
      // Exit edit mode
      setEditingFieldId(null);
      
      // DO NOT clear fieldValues yet - keep the saved value so it displays immediately
      // The value will be cleared when the component updates with fresh data from the server
      
      if (onFieldUpdated) {
        onFieldUpdated();
      }
    } catch (error: any) {
      console.error('❌ Error updating field:', error);
      
      // Extract the error message from the API response
      let errorMessage = 'Failed to update field';
      let errorCode = '';
      
      if (error.apiError) {
        // Use the structured API error
        errorMessage = error.apiError.message || errorMessage;
        errorCode = error.apiError.errorCode || '';
      } else if (error.response?.data?.message) {
        // Fallback to response data
        errorMessage = error.response.data.message;
        errorCode = error.response.data.errorCode || '';
      } else if (error.message) {
        // Fallback to error message
        errorMessage = error.message;
      }
      
      // Show detailed error to user
      const alertMessage = `Error updating ${field.label}\n\n${errorMessage}${errorCode ? `\n\nError Code: ${errorCode}` : ''}`;
      alert(alertMessage);
      
      // Don't exit edit mode on error so user can try again
    }
  };

  const handleValueChange = (fieldId: string, value: any) => {
    setFieldValues({
      ...fieldValues,
      [fieldId]: value,
    });
  };

  const renderFieldInput = (field: OrderAttributeFieldDto) => {
    const value = fieldValues[field.editableFieldId] ?? field.currentValue ?? '';
    const isEditing = editingFieldId === field.editableFieldId;

    if (!isEditing) {
      return (
        <div className="flex items-center justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700 dark:text-secondary-200">{field.label}</span>
              {field.isRequired && <span className="text-red-500 text-xs">*</span>}
              {!field.isEditable && (
                <i className="fa-solid fa-eye text-gray-400 dark:text-secondary-500 text-xs" title="Display only"></i>
              )}
              {field.description && (
                <span className="text-xs text-gray-500 dark:text-secondary-400">({field.description})</span>
              )}
            </div>
            <div className="text-sm text-gray-900 dark:text-white truncate">
              {value || <span className="text-gray-400 dark:text-secondary-500 italic">No value</span>}
            </div>
          </div>
          {field.canEdit && isActiveApprover ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleStartEdit(field)}
              className="flex-shrink-0"
              title="Edit field"
            >
              <i className="fa-solid fa-pen-to-square"></i>
            </Button>
          ) : field.isEditable ? (
            <div className="flex-shrink-0 text-gray-400 dark:text-secondary-500 px-3" title="Cannot edit at this approval level">
              <i className="fa-solid fa-lock text-sm"></i>
            </div>
          ) : null}
        </div>
      );
    }

    return (
      <div className="space-y-2">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-secondary-200 mb-1">
            {field.label}
            {field.isRequired && <span className="text-red-500 ml-1">*</span>}
          </label>
          {field.description && (
            <div className="text-xs text-gray-500 dark:text-secondary-400 mb-1">{field.description}</div>
          )}
          {renderInput(field, value, (val) => handleValueChange(field.editableFieldId, val))}
        </div>
        <div className="flex space-x-2">
          <Button
            onClick={() => handleSaveField(field)}
            disabled={updateFieldMutation.isPending}
            size="sm"
          >
            {updateFieldMutation.isPending ? 'Saving...' : 'Save'}
          </Button>
          <Button
            variant="outline"
            onClick={() => handleCancelEdit(field.editableFieldId)}
            disabled={updateFieldMutation.isPending}
            size="sm"
          >
            Cancel
          </Button>
        </div>
      </div>
    );
  };

  const renderInput = (field: OrderAttributeFieldDto, value: any, onChange: (value: any) => void) => {
    // If the order has allowed-value constraints for this attribute, render a filterable select
    if (field.allowedValues && field.allowedValues.length > 0) {
      return (
        <FilterableSelect
          value={value ?? ''}
          onChange={onChange}
          options={field.allowedValues}
          showValueInList={!!field.showValueInList}
          required={field.isRequired}
        />
      );
    }

    switch (field.fieldType) {
      case 'number':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => onChange(e.target.valueAsNumber || 0)}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-secondary-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
            required={field.isRequired}
          />
        );
      case 'boolean':
        return (
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={!!value}
              onChange={(e) => onChange(e.target.checked)}
              className="h-4 w-4 text-blue-600 dark:text-blue-400 focus:ring-blue-500 border-gray-300 dark:border-secondary-600 rounded bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
            />
            <label className="ml-2 text-sm text-gray-700 dark:text-secondary-200">
              {value ? 'Yes' : 'No'}
            </label>
          </div>
        );
      case 'date':
        return (
          <input
            type="date"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-secondary-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
            required={field.isRequired}
          />
        );
      case 'string':
      default:
        return (
          <textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-secondary-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
            rows={2}
            required={field.isRequired}
          />
        );
    }
  };

  if (!orderAttributes || orderAttributes.length === 0) {
    return null; // Don't show the section if there are no order attributes
  }

  return (
    <Card>
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">Order Attributes</h3>
          {modifications && modifications.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowHistory(!showHistory)}
            >
              <i className={`fa-solid ${showHistory ? 'fa-eye-slash' : 'fa-clock-rotate-left'} mr-1`}></i>
              {showHistory ? 'Hide' : 'History'}
            </Button>
          )}
        </div>

        <div className="space-y-2">
          {orderAttributes.map((field) => (
            <div
              key={field.editableFieldId}
              className="p-2 border border-gray-200 dark:border-secondary-700 rounded hover:border-gray-300 dark:hover:border-secondary-500 transition-colors"
            >
              {renderFieldInput(field)}
            </div>
          ))}
        </div>

        {showHistory && modifications && modifications.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-secondary-700">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Modification History</h4>
            <div className="space-y-1.5">
              {modifications.map((mod) => (
                <div
                  key={mod.id}
                  className="text-xs p-2 bg-gray-50 dark:bg-secondary-900 rounded border border-gray-200 dark:border-secondary-700"
                >
                  <div className="flex justify-between items-start mb-1">
                    <span className="font-medium">{mod.fieldLabel}</span>
                    <Badge variant="info" className="text-xs">
                      Step {mod.step}
                    </Badge>
                  </div>
                  <div className="text-gray-600 dark:text-secondary-400">
                    <span className="line-through">{mod.originalValue || 'empty'}</span>
                    {' → '}
                    <span className="font-medium">{mod.newValue}</span>
                  </div>
                  <div className="text-gray-500 dark:text-secondary-400">
                    {mod.modifiedBy} • {formatDate(mod.modifiedAt)}
                  </div>
                  {mod.modificationReason && (
                    <div className="mt-1 text-gray-600 dark:text-secondary-400 italic">
                      {mod.modificationReason}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

// Legacy alias for backward compatibility
export const EditableFieldsSection = OrderAttributesSection;
